import { CARDIO_BY_ID, CARDIO_TYPES, estimateCalories, HR_ZONES, zoneRange, type CardioType } from '../data/cardio';
import { MUSCLES } from '../data/muscles';
import type { SessionTemplate } from '../data/splits';
import { daysBetween, dayName, todayIso } from '../lib/dates';
import type { AppState, CardioSession, WorkoutSession } from '../store/types';
import { blockInfo, type BlockInfo } from './blocks';
import { groupFreshness, muscleStatuses, type MuscleStatus } from './recovery';

export type ScheduledKind = 'lift' | 'cardio' | 'rest';

export interface SessionOption {
  template: SessionTemplate;
  /** Days since you last did this exact session. Undefined if never. */
  daysSince?: number;
  /** 0–1 readiness of this session's focus muscles. */
  freshness: number;
  score: number;
  reason: string;
  /** True when the focus muscles are still meaningfully sore. */
  underRecovered: boolean;
}

export interface CardioPlan {
  type: CardioType;
  minutes: number;
  estimatedCalories: number;
  zoneLabel: string;
  zoneBpm: [number, number];
  reason: string;
}

export interface DayPlan {
  date: string;
  scheduled: ScheduledKind;
  block: BlockInfo;
  headline: string;
  subline: string;
  recommendedLift?: SessionOption;
  liftOptions: SessionOption[];
  cardio?: CardioPlan;
  warnings: string[];
  loggedLift?: WorkoutSession;
  loggedCardio: CardioSession[];
  statuses: Record<string, MuscleStatus>;
}

/** Below this, the focus muscles are still beaten up enough to matter. */
const UNDER_RECOVERED = 0.75;

export function scheduledKind(state: AppState, dateIso: string): ScheduledKind {
  const day = new Date(`${dateIso}T12:00:00`).getDay();
  if (state.profile.liftDays.includes(day)) return 'lift';
  if (state.profile.cardioDays.includes(day)) return 'cardio';
  return 'rest';
}

export function planDay(state: AppState, dateIso: string = todayIso(), now: Date = new Date()): DayPlan {
  const block = blockInfo(state.settings, dateIso);
  const statuses = muscleStatuses(state.workouts, now);
  const scheduled = scheduledKind(state, dateIso);

  const loggedLift = state.workouts.find((w) => w.date === dateIso && w.finishedAt);
  const loggedCardio = state.cardio.filter((c) => c.date === dateIso);

  const liftOptions = rankSessions(state, block, statuses, dateIso);
  const recommendedLift = liftOptions[0];
  const cardio = planCardio(state, dateIso, statuses);

  const warnings = buildWarnings(state, statuses, recommendedLift, block);

  const { headline, subline } = describe(
    scheduled,
    block,
    recommendedLift,
    cardio,
    loggedLift,
    loggedCardio,
    dateIso,
  );

  return {
    date: dateIso,
    scheduled,
    block,
    headline,
    subline,
    recommendedLift,
    liftOptions,
    cardio,
    warnings,
    loggedLift,
    loggedCardio,
    statuses,
  };
}

/**
 * Ranks the three sessions in the current block.
 *
 * The big idea: don't drive off the calendar. If you skip Monday, a fixed
 * weekday schedule would have you miss chest for a week. Instead we pick
 * whichever session you've gone longest without, then veto anything whose
 * muscles are still wrecked. Miss a day and the rotation just shifts — nothing
 * gets dropped.
 */
function rankSessions(
  state: AppState,
  block: BlockInfo,
  statuses: Record<string, MuscleStatus>,
  dateIso: string,
): SessionOption[] {
  const finished = state.workouts.filter((w) => w.finishedAt);

  const options = block.split.sessions.map((template, index) => {
    const last = finished
      .filter((w) => w.templateId === template.id)
      .sort((a, b) => (a.date < b.date ? 1 : -1))[0];

    const daysSince = last ? daysBetween(last.date, dateIso) : undefined;
    const freshness = groupFreshness(statuses, template.focusMuscles);
    const underRecovered = freshness < UNDER_RECOVERED;

    // Longest gap wins. Never done in this split at all counts as very overdue.
    let score = daysSince === undefined ? 30 : daysSince * 4;

    // Recovery is a veto, not a nudge: doing a hard leg day on trashed quads is
    // worse than doing the session you're slightly less "due" for.
    score += (freshness - 1) * 40;
    if (freshness < 0.5) score -= 40;

    // Same day, don't repeat what you just did.
    if (daysSince === 0) score -= 100;

    // Keeps the intended order when you're starting a fresh block with no
    // history to go on.
    score -= index * 0.5;

    return {
      template,
      daysSince,
      freshness,
      score,
      underRecovered,
      reason: explainOption(template, daysSince, freshness),
    };
  });

  return options.sort((a, b) => b.score - a.score);
}

function explainOption(
  template: SessionTemplate,
  daysSince: number | undefined,
  freshness: number,
): string {
  const pct = Math.round(freshness * 100);
  if (daysSince === undefined) {
    return `You haven't done ${template.name} in this program yet. Focus muscles are ${pct}% recovered.`;
  }
  if (daysSince === 0) return 'You already did this one today.';
  const when = daysSince === 1 ? 'yesterday' : `${daysSince} days ago`;
  if (freshness < UNDER_RECOVERED) {
    return `Last done ${when}, but the focus muscles are only ${pct}% recovered — leave it another day if you can.`;
  }
  return `Last done ${when}. Focus muscles are ${pct}% recovered and ready.`;
}

/**
 * Cardio picker. Two rules do most of the work: rotate so you're not on the
 * same machine every time, and keep high-impact work away from leg day.
 */
function planCardio(
  state: AppState,
  dateIso: string,
  statuses: Record<string, MuscleStatus>,
): CardioPlan | undefined {
  const { profile } = state;
  const available = new Set(state.settings.equipment);

  const legsFresh = Math.min(
    statuses['quads']?.freshness ?? 1,
    statuses['hamstrings']?.freshness ?? 1,
  );

  // Is a lifting day coming tomorrow? If so, spare the legs today.
  const tomorrow = new Date(`${dateIso}T12:00:00`);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const liftTomorrow = profile.liftDays.includes(tomorrow.getDay());

  const recentIds = [...state.cardio]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 2)
    .map((c) => c.typeId);

  const hiitThisWeek = state.cardio.filter(
    (c) => daysBetween(c.date, dateIso) < 7 && CARDIO_BY_ID[c.typeId]?.style === 'interval',
  ).length;

  const candidates = CARDIO_TYPES.filter(
    (c) => c.equipment === 'bodyweight' || available.has(c.equipment),
  );
  if (candidates.length === 0) return undefined;

  const scored = candidates.map((type) => {
    let score = 10;

    // Rotation — don't offer the same machine three times running.
    const idx = recentIds.indexOf(type.id);
    if (idx === 0) score -= 5;
    else if (idx === 1) score -= 2;

    // Leg protection.
    if (type.legImpact === 'high') {
      if (legsFresh < 0.8 || liftTomorrow) score -= 7;
      if (hiitThisWeek >= 2 && type.style === 'interval') score -= 6;
    } else if (type.legImpact === 'medium') {
      if (legsFresh < 0.6) score -= 3;
    } else {
      score += 1;
    }

    // Fat loss favours long, low-impact work you can repeat without cost.
    if (profile.goal === 'fat_loss' && type.legImpact === 'low') score += 3;
    if (profile.goal === 'strength' && type.style === 'interval') score -= 2;

    return { type, score };
  });

  const best = scored.sort((a, b) => b.score - a.score || a.type.id.localeCompare(b.type.id))[0];
  if (!best) return undefined;

  const type = best.type;
  const zone = type.style === 'interval' ? HR_ZONES[3]! : HR_ZONES[1]!;
  const minutes = type.defaultMinutes;

  const reasons: string[] = [];
  if (type.legImpact === 'low' && (liftTomorrow || legsFresh < 0.8)) {
    reasons.push(
      liftTomorrow
        ? 'picked something low-impact because you lift tomorrow'
        : 'picked something low-impact because your legs are still recovering',
    );
  }
  if (recentIds.length && !recentIds.includes(type.id)) {
    reasons.push('a change from your last couple of sessions');
  }
  if (profile.goal === 'fat_loss' && type.legImpact === 'low') {
    reasons.push('it burns well without costing you anything in the weight room');
  }

  return {
    type,
    minutes,
    estimatedCalories: estimateCalories(type.met, profile.bodyweightKg, minutes),
    zoneLabel: `Zone ${zone.n} — ${zone.name}`,
    zoneBpm: zoneRange(profile.age, zone),
    reason: reasons.length
      ? `${reasons[0]!.charAt(0).toUpperCase()}${reasons[0]!.slice(1)}${reasons[1] ? `, and ${reasons[1]}` : ''}.`
      : type.bestFor,
  };
}

function buildWarnings(
  state: AppState,
  statuses: Record<string, MuscleStatus>,
  recommended: SessionOption | undefined,
  block: BlockInfo,
): string[] {
  const warnings: string[] = [];

  if (recommended?.underRecovered) {
    const sore = recommended.template.focusMuscles
      .map((m) => statuses[m])
      .filter((s): s is MuscleStatus => Boolean(s) && s!.freshness < UNDER_RECOVERED)
      .sort((a, b) => a.freshness - b.freshness)
      .slice(0, 3)
      .map((s) => `${MUSCLES[s.muscle].short} ${Math.round(s.freshness * 100)}%`);
    if (sore.length) {
      warnings.push(
        `Still recovering: ${sore.join(', ')}. This is your best option anyway — just take the first sets easier.`,
      );
    }
  }

  const over = Object.values(statuses)
    .filter((s) => s.volumeState === 'over')
    .sort((a, b) => b.weeklySets - b.target[1] - (a.weeklySets - a.target[1]))
    .slice(0, 2);
  for (const s of over) {
    warnings.push(
      `${MUSCLES[s.muscle].short}: ${s.weeklySets} sets in the last 7 days, above the ${s.target[1]}-set ceiling. More is not better here.`,
    );
  }

  if (block.isDeload) {
    warnings.push('Deload week — the plan is deliberately light. Resist the urge to push it.');
  }

  const lastSeven = state.workouts.filter(
    (w) => w.finishedAt && daysBetween(w.date, todayIso()) < 7,
  ).length;
  if (lastSeven >= 5) {
    warnings.push(`${lastSeven} lifting sessions in the last 7 days. Take a full rest day.`);
  }

  return warnings;
}

function describe(
  scheduled: ScheduledKind,
  block: BlockInfo,
  lift: SessionOption | undefined,
  cardio: CardioPlan | undefined,
  loggedLift: WorkoutSession | undefined,
  loggedCardio: CardioSession[],
  dateIso: string,
): { headline: string; subline: string } {
  const today = dayName(new Date(`${dateIso}T12:00:00`).getDay());

  if (loggedLift) {
    return {
      headline: 'Done for today',
      subline: `You finished ${loggedLift.templateName}. Eat, sleep, come back ${block.isDeload ? 'easy' : 'strong'}.`,
    };
  }

  if (scheduled === 'lift' && lift) {
    return {
      headline: lift.template.name,
      subline: `${today} · ${lift.template.focus}`,
    };
  }

  if (scheduled === 'cardio') {
    if (loggedCardio.length > 0) {
      const total = loggedCardio.reduce((t, c) => t + c.calories, 0);
      return {
        headline: 'Cardio done',
        subline: `${loggedCardio.length} session${loggedCardio.length > 1 ? 's' : ''} logged, about ${total} kcal.`,
      };
    }
    return {
      headline: cardio ? cardio.type.name : 'Cardio day',
      subline: cardio ? `${today} · ${cardio.minutes} min · ${cardio.zoneLabel}` : today,
    };
  }

  return {
    headline: 'Rest day',
    subline: lift
      ? `Nothing scheduled. If you do want to train, ${lift.template.name} is next up.`
      : 'Nothing scheduled. Recovery is where the growth actually happens.',
  };
}

/** The next seven days at a glance, for the week strip on the Today screen. */
export interface WeekDay {
  date: string;
  dayNumber: number;
  kind: ScheduledKind;
  isToday: boolean;
  done: boolean;
}

export function weekAhead(state: AppState, fromIso: string = todayIso()): WeekDay[] {
  const start = new Date(`${fromIso}T12:00:00`);
  const days: WeekDay[] = [];
  // Start from Monday of the current week.
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));

  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const iso = todayIso(d);
    const kind = scheduledKind(state, iso);
    days.push({
      date: iso,
      dayNumber: d.getDay(),
      kind,
      isToday: iso === fromIso,
      done:
        kind === 'lift'
          ? state.workouts.some((w) => w.date === iso && w.finishedAt)
          : kind === 'cardio'
            ? state.cardio.some((c) => c.date === iso)
            : false,
    });
  }
  return days;
}
